import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from '../../assets/icons/ArrowLeft';
import { CircleExclamationIcon } from '../../assets/icons/CircleExclamationIcon';
import { PageBackgroundWrapper } from '../../components/PageBackgroundWrapper';
import { iconPalette } from '../../const/iconPalette';
import { FormKey } from '../../enums/formKey';
import { StepKey } from '../../enums/stepKey';
import { StoreEvent } from '../../enums/storeEvent';
import { CatalogueAppliance, CatalogueCategory } from '../../models/services/api/getCatalogue';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { useApplianceFormData } from '../../store/selectors/useApplianceFormData';
import { useAppState } from '../../store/selectors/useAppState';
import { useStoreContext } from '../../store/selectors/useStoreContext';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { store } from '../../store/store';
import { getSystemCode } from '../../utils/getSystemCode';

export const styles = {
  grid: 'grid gap-space-500 grid-cols-2 md:grid-cols-3',
  categoryGridRows: 'auto-rows-[120px]',
  applianceGridRows: 'auto-rows-[70px]',
  applianceTile:
    'py-space-500 px-space-300 text-center border-1 cursor-pointer button-border-outline-default text-text-primary-500 flex justify-center items-center flex-col',
  applianceTileButton:
    'rounded-radius-200 border-button-border-outline-default disabled:bg-(--button-bg-outline-disabled-color) disabled:text-(--button-text-outline-disabled-color)',
  error: 'bg-(--bg-error) border-(--input-border-error-color)',
  focus:
    'focus-visible:border-0 focus-visible:outline-3 focus-visible:inset-ring-3 focus-visible:border-button-border-primary-focus focus-visible:outline-focus-900 focus-visible:inset-ring-focus-600',
  hover:
    'hover:text-button-text-outline-hover hover:bg-button-bg-outline-hover hover:[&_svg]:text-(--color-icon-button-outline-active)',
  active:
    'active:[&_svg]:text-(--color-icon-button-outline-active) active:bg-button-bg-outline-hover active:text-button-text-outline-active',
  selected:
    '[&_svg]:text-(--color-icon-button-outline-active) bg-button-bg-outline-active text-button-text-outline-active',
  unselected: 'button-border-outline-default [&_svg]:text-(--color-icon-button-outline-default)',

  selectedApplianceType: (isSelected: boolean) =>
    isSelected
      ? styles['selected'] + ' ' + 'px-space-200'
      : styles['unselected'] + ' ' + ' px-space-300',
};

export function ApplianceDetailsPage({
  stepNumber,
  totalSteps,
}: {
  stepNumber: number;
  totalSteps: number;
}) {
  const { session, catalogue } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    formData[StepKey.APPLIANCE_DETAILS]?.[FormKey.SUPER_CATEGORY] || null,
  );
  const [selectedAppliance, setSelectedApplianceCode] = useState<string | null>(
    formData[StepKey.APPLIANCE_DETAILS]?.[FormKey.ITEM_CATEGORY] || null,
  );
  const [isApplianceError, setIsApplianceError] = useState<boolean>(false);
  const [isCategoryError, setIsCategoryError] = useState<boolean>(false);
  const posthog = usePostHog();
  const [applianceCategory, setApplianceCategory] = useState<string | undefined>(undefined);
  const [applianceName, setApplianceName] = useState<string | undefined>(undefined);
  const storeContext = useStoreContext();

  const scrollIntoView = useCallback((el: HTMLElement | null) => {
    if (el) smoothScrollIntoView(el);
  }, []);

  useEffect(() => {
    setSelectedCategory(formData[StepKey.APPLIANCE_DETAILS]?.[FormKey.SUPER_CATEGORY] || null);
    setSelectedApplianceCode(formData[StepKey.APPLIANCE_DETAILS]?.[FormKey.ITEM_CATEGORY] || null);
  }, [formData]);

  useEffect(() => {
    gaPageView({
      pagename: 'olr-appliance-type',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
    });
  }, []);

  const errorMessage = () => {
    if (isApplianceError || isCategoryError) {
      return (
        <div className="text-error-600 mt-2 flex items-center gap-1">
          <CircleExclamationIcon />
          <p>Please select a product {selectedCategory ? 'type' : 'category'}</p>
        </div>
      );
    }
  };

  const CategoryIcon = ({ category }: { category: string }) => {
    const Icon = iconPalette[category?.toLowerCase()];
    return Icon && <Icon />;
  };

  const appliances = catalogue?.find((cat) => cat.categoryCode === selectedCategory)?.appliances;

  const handleContinue = () => {
    gaEvent({
      pagename: 'olr-appliance-type',
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: !selectedCategory || !selectedAppliance ? 'failed' : 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      appSessionID: session?.id ?? '',
    });

    capturePostHogEvent(posthog, 'appliance-details-continue-cta-clicked', storeContext);

    if (applianceName) {
      capturePostHogEvent(
        posthog,
        `${selectedCategory?.toLowerCase()}-${[...applianceName.trim()].reduce((acc, current) => {
          if (current === ',') return acc;
          if (current === ' ' || current === '/') return acc + '-';
          return acc + current.toLowerCase();
        }, '')}-selected`,
        storeContext,
      );
    }

    if (!selectedCategory) {
      setIsCategoryError(true);
      return;
    }

    if (!selectedAppliance) {
      setIsApplianceError(true);
      return;
    }

    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.APPLIANCE_DETAILS]: {
          [FormKey.SUPER_CATEGORY]: selectedCategory,
          [FormKey.ITEM_CATEGORY]: selectedAppliance,
        },
      },
    });
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      pagename: 'olr-appliance-type',
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'Go back',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      appSessionID: session?.id ?? '',
    });
    capturePostHogEvent(posthog, 'appliance-details-back-clicked', storeContext);
    store.send({ type: StoreEvent.BACK, data: { stepKey: StepKey.APPLIANCE_DETAILS } });
  };

  const handleCategorySelect = (categoryCode: string) => {
    if (selectedCategory !== categoryCode) {
      setSelectedCategory(categoryCode);
      setSelectedApplianceCode(null);
      setIsCategoryError(false);

      const applianceCategoryName = catalogue?.find(
        (category) => category.categoryCode === categoryCode,
      )?.categoryName;

      setApplianceCategory(applianceCategoryName);
      gaEvent({
        pagename: 'olr-appliance-type',
        eventCategory: 'appliance details form USA',
        eventAction: 'applianceCategoryCode',
        eventLabel: categoryCode,
        category: 'registration-pages',
        type: 'STANDARD',
        applianceCategory: applianceCategoryName ?? applianceCategory,
        appSessionID: session?.id,
        regSystemCode: systemCode,
      });
      capturePostHogEvent(posthog, 'super-appliance-selected', storeContext);
    }
  };

  const handleApplianceSelect = (applianceCode: string) => {
    if (selectedAppliance !== applianceCode) {
      setSelectedApplianceCode(applianceCode);
      setIsApplianceError(false);

      const appliances =
        catalogue?.find((category) => category.categoryCode === selectedCategory)?.appliances ?? [];

      const applianceName = appliances.find(
        (appliance) => appliance.applianceCode === applianceCode,
      )?.applianceName;

      setApplianceName(applianceName);

      gaEvent({
        pagename: 'olr-appliance-type',
        eventCategory: 'appliance details form USA',
        eventAction: 'applianceCode',
        eventLabel: applianceCode,
        category: 'registration-pages',
        type: 'STANDARD',
        applianceCategory,
        appSessionID: session?.id,
        regSystemCode: systemCode,
      });
      capturePostHogEvent(posthog, 'sub-appliance-selected', storeContext);
    }
  };

  return (
    <>
      <PageBackgroundWrapper gapSize="500">
        <ProgressBars total={totalSteps} current={stepNumber} />
        <h1 className="text-h3 text-text-headline">What type of product are you registering?</h1>
        <p className="text-body-large-strong">Select a product category</p>
        <div className={`${styles['categoryGridRows']} ${styles['grid']}`}>
          {catalogue &&
            catalogue.map((category: CatalogueCategory) => (
              <button
                key={category.categoryCode}
                aria-pressed={selectedCategory === category.categoryCode}
                id={`category-${category.categoryCode.toLocaleLowerCase()}`}
                value={category.categoryCode}
                type="button"
                className={`w-full
                    ${styles['selectedApplianceType'](selectedCategory === category.categoryCode)}
                    ${isCategoryError && styles['error']}
                    ${styles['applianceTile']}
                    ${styles['applianceTileButton']}
                    ${styles['focus']}
                    ${styles['hover']}
                    ${styles['active']}
                   `}
                onClick={() => handleCategorySelect(category.categoryCode)}
              >
                <div className="mb-space-100">
                  <CategoryIcon category={category.categoryCode.toLowerCase()} />
                </div>
                <p
                  className={`${selectedCategory === category.categoryCode ? 'text-body-small-strong' : 'text-body-small'}`}
                >
                  {category.categoryName}
                </p>
              </button>
            ))}
        </div>

        {appliances ? (
          <div ref={(el) => scrollIntoView(el)} className="mt-4">
            <p className="mb-4 text-body-large-strong">Select a product type</p>
            <div className={`${styles['applianceGridRows']} ${styles['grid']}`}>
              {appliances.map((appliance: CatalogueAppliance) => (
                <button
                  key={appliance.applianceCode}
                  aria-pressed={selectedAppliance === appliance.applianceCode}
                  id={`appliance-${appliance.applianceCode}`}
                  value={appliance.applianceCode}
                  type="button"
                  className={`
                      ${styles['selectedApplianceType'](selectedAppliance === appliance.applianceCode)}
                      ${isApplianceError && styles['error']}
                      ${styles['applianceTile']}
                      ${styles['applianceTileButton']}
                      ${styles['focus']}
                      ${styles['hover']}
                    `}
                  onClick={() => handleApplianceSelect(appliance.applianceCode)}
                >
                  <p
                    className={`${selectedAppliance === appliance.applianceCode ? 'text-body-small-strong' : 'text-body-small'}`}
                  >
                    {appliance.applianceName}
                  </p>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {errorMessage()}

        <div className="flex flex-col w-full mt-4 items-center md:flex-row-reverse md:justify-between">
          <div className="w-full md:w-fit mb-4">
            <Button onClick={handleContinue}>Continue</Button>
          </div>
          <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
            Go back
          </TextLink>
        </div>
      </PageBackgroundWrapper>
    </>
  );
}
